import { lazy as reactLazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Cookies from "js-cookie";
import Loader from "../components/loader/Loading";
import Notification from "../pages/notification/Notification";
import PriceList from "../pages/reporting/PriceList";
import Scrap from "../pages/scrap/Scrap";
import ManageStages from "../pages/subItems/ManageStages";
import ProfitLossReport from "../pages/reporting/ProfitLossReport";

// UI-09: a page's code that fails to download (weak mobile network, or a new release replaced the files) reloads the
// app once instead of leaving a blank screen
const retryFlag = (action) => {
  try {
    if (action === "get") return sessionStorage.getItem("sgew-chunk-retry");
    if (action === "set") sessionStorage.setItem("sgew-chunk-retry", "1");
    else sessionStorage.removeItem("sgew-chunk-retry");
  } catch (e) {
    return "1"; // no storage (private mode): do not risk a reload loop
  }
  return null;
};
const lazy = (load) =>
  reactLazy(() =>
    load()
      .then((module) => {
        retryFlag("clear");
        return module;
      })
      .catch((error) => {
        if (!retryFlag("get")) {
          retryFlag("set");
          window.location.reload();
          return new Promise(() => {});
        }
        throw error;
      })
  );

// Lazy load components
const ProtectedRoute = lazy(() => import("./ProtectedRoute"));
const Login = lazy(() => import("../pages/login"));
const HomePage = lazy(() => import("../pages/home/Home"));
const Dashboard = lazy(() => import("../pages/dashboard"));
const PageNotFound = lazy(() => import("../pages/pageNotFound/Index"));
const User = lazy(() => import("../pages/userManagement"));
const Landingpage = lazy(() => import("../pages/landingpage/Landingpage"));
const Profile = lazy(() => import("../pages/profile/Profile"));
const UserDetails = lazy(() => import("../pages/userManagement/UserDetails"));
const UserTable = lazy(() => import("../pages/userManagement/UserTable"));
const Access = lazy(() => import("../pages/access/Index"));
const AllRoleAccess = lazy(() => import("../pages/access/AllRoleAccess"));
const Setting = lazy(() => import("../pages/profile/Setting"));
const Items = lazy(() => import("../pages/items/index"));
const Products = lazy(() => import("../pages/products/index"));
const ManageCategory = lazy(() => import("../pages/items/ManageCategory"));
const AllItems = lazy(() => import("../pages/items/AllItems"));
const AllSubItems = lazy(() => import("../pages/subItems/AllSubItems"));
const SessionTimeout = lazy(() =>
  import("../components/sessionScreen/SessionTimeout")
);
const Index = lazy(() => import("../pages/purchase"));
const AllPurchaseOrders = lazy(() =>
  import("../pages/purchase/AllPurchaseOrders")
);
const PurchaseOrderForm = lazy(() => import("../pages/purchase/ViewAndEdit"));
const Category = lazy(() => import("../pages/products/Category"));
const Management = lazy(() => import("../pages/products/Management"));
const PartsManagement = lazy(() => import("../pages/products/PartsManagement"));
const CreateProduct = lazy(() => import("../pages/products/CreateProduct"));
const CreatePart = lazy(() => import("../pages/products/CreatePart"));
const Orders = lazy(() => import("../pages/orders/index"));
const Payment = lazy(() => import("../pages/payment/index"));
const OrderManagement = lazy(() => import("../pages/orders/OrderManagement"));
const CreateOrder = lazy(() => import("../pages/orders/CreateOrder"));
const DealerManagement = lazy(() => import("../pages/orders/DealerManagement"));
const PaymentManagement = lazy(() =>
  import("../pages/payment/PaymentManagement")
);
const Attendance = lazy(() => import("../pages/attendence"));
const AttendanceTable = lazy(() =>
  import("../pages/attendence/AttendanceTable")
);
const TransactionMain = lazy(() =>
  import("../components/DataTable/TransactionHistoryTable")
);
const Otherincome = lazy(() => import("../pages/payment/Otherincome"));
const ServiceManagement = lazy(() =>
  import("../pages/orders/ServiceManagement")
);
const CreateService = lazy(() => import("../pages/orders/CreateService"));
const WarrantyManagement = lazy(() =>
  import("../pages/orders/WarrantyManagement")
);
const Assets = lazy(() => import("../pages/assets"));
const AssetsTable = lazy(() => import("../pages/assets/AssetsTable"));
const Allreporting = lazy(() => import("../pages/reporting/Allreporting"));
const MainDashboard = lazy(() => import("../pages/dashboard/MainDashboard"));
const Productperformance = lazy(() =>
  import("../pages/reporting/Productperformance")
);
const Estimatedreport = lazy(() =>
  import("../pages/reporting/Estimatedreport")
);
const Purchaseperformance = lazy(() =>
  import("../pages/reporting/Purchaseperformance")
);
const Paymentdue = lazy(() => import("../pages/reporting/Paymentdue"));
const ItemPerformance = lazy(() => import("../pages/reporting/ItemPerformance")); // P034
const StockAlerts = lazy(() => import("../pages/reporting/StockAlerts")); // P035
const Reminders = lazy(() => import("../pages/reporting/Reminders")); // P036
const DealerPortal = lazy(() => import("../pages/dealer/DealerPortal")); // SGEW 17 Oct: reseller portal
const WebsiteEnquiries = lazy(() => import("../pages/reporting/WebsiteEnquiries")); // SGEW 17 Oct: the website's enquiries
const Allapprovals = lazy(() => import("../pages/approvals/Allapprovals"));
const EmployeesReport = lazy(() =>
  import("../pages/employeereport/EmployeesReport")
);

// UI-09: decided when "/" is opened, not when the app first rendered (a login or logout since then was missed)
const RootRoute = () => (Cookies.get("token") ? <Navigate to="/dashboard" replace /> : <Landingpage />);

const AppRoutes = () => {
  return (
    <div className="m-0 p-0 bg-[#f9f9fb]">
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />

          <Route element={<ProtectedRoute element={<HomePage />} />}>
            <Route path="dashboard" element={<Dashboard />}>
              <Route index element={<MainDashboard />} />
              <Route path="reporting" element={<Allreporting />} />
              <Route path="approvals" element={<Allapprovals />} />
              <Route
                path="reporting/product-performance"
                element={<Productperformance />}
              />
              <Route path="reporting/price-list" element={<PriceList />} /> 
               <Route path="reporting/profit-loss-report" element={<ProfitLossReport />} />
              <Route
                path="reporting/product-report"
                element={<Estimatedreport />}
              />
              <Route
                path="reporting/purchase-performance"
                element={<Purchaseperformance />}
              />
              <Route
                path="reporting/payment-due-report"
                element={<Paymentdue />}
              />
              <Route path="reporting/item-subitem-performance" element={<ItemPerformance />} />
              <Route path="reporting/stock-alert" element={<StockAlerts />} />
              <Route path="reporting/reminder" element={<Reminders />} />
              <Route path="reporting/dealer-stock-sales" element={<DealerPortal />} />
              <Route path="reporting/enquiries" element={<WebsiteEnquiries />} />
            </Route>
            <Route path="users" element={<User />}>
              <Route path="" element={<UserTable />} />
              <Route path="users-list" element={<UserTable />} />
            </Route>
            <Route path="access-management" element={<Access />}>
              <Route path="" element={<AllRoleAccess />} />
            </Route>

            <Route path="purchases" element={<Index />}>
              <Route path="" element={<AllPurchaseOrders />} />
              <Route
                path="edit-purchase-order/:id"
                element={<PurchaseOrderForm />}
              />
            </Route>

            <Route path="attendance" element={<Attendance />}>
              <Route path="" element={<AttendanceTable />} />
              <Route path="employee-report" element={<EmployeesReport />} />
            </Route>

            <Route path="items" element={<Items />}>
              <Route path="" element={<AllItems />} />
              <Route path="scrap" element={<Scrap />} />
              <Route path="assets" element={<Assets />}>
                <Route path="" element={<AssetsTable />} />
              </Route>
              <Route path="categories" element={<ManageCategory />} />
              <Route path="subitems" element={<AllSubItems />} /> 
               <Route path="stages" element={<ManageStages/>} /> 
            </Route>

            <Route path="manufacturing" element={<Products />}>
              <Route path="" element={<Management />} />
              <Route path="add-product" element={<Category />} />
              <Route path="parts-management" element={<PartsManagement />} />
              <Route path="create-product" element={<CreateProduct />} />
              <Route path="create-parts" element={<CreatePart />} />
              <Route
                path="edit-part/:productId"
                element={<CreatePart />}
              />
              <Route
                path="edit-product/:productId"
                element={<CreateProduct />}
              />
            </Route>

            <Route path="sales-service" element={<Orders />}>
              <Route path="" element={<OrderManagement />} />
              <Route path="dealer-order" element={<DealerManagement />} />
              {/* <Route path="manage-orders" element={<OrderManagement />} /> */}
              <Route path="create-order" element={<CreateOrder />} />
              <Route path="edit-order/:orderId" element={<CreateOrder />} />
              <Route path="product-service" element={<ServiceManagement />} />
              <Route path="create-service" element={<CreateService />} />
              <Route path="my-stock" element={<DealerPortal />} />
              <Route path="my-sales" element={<DealerPortal />} />
              <Route
                path="edit-service/:serviceId"
                element={<CreateService />}
              />
              <Route path="manage-warranty" element={<WarrantyManagement />} />
            </Route>

            <Route path="payment" element={<Payment />}>
              <Route path="" element={<PaymentManagement />} />
              <Route
                path="payment-history/:paymentId"
                element={<TransactionMain />}
              />
              <Route path="income-management" element={<Otherincome />} />
            </Route>

            <Route path="users/user-detail/:id" element={<UserDetails />} />
            <Route path="/profile/:id" element={<Profile />} />
            <Route path="/settings/:id" element={<Setting />} />
            <Route path="/notification" element={<Notification />} />
          </Route>

          <Route path="/session-timeout" element={<SessionTimeout />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default AppRoutes;
